import React, {
  FC, useEffect, useState, useContext, useMemo
} from 'react'
import { useHistory, useLocation, Redirect } from 'react-router-dom'
import { ChevronLeftIcon } from '@heroicons/react/solid'
import { useNotifications } from '../../Notifications'
import { Button, Checkbox, LabelWrapper } from '../../System'
import { UpdateCardInfo } from '../../User'
import { StyledPaymentPage } from '..'
import { PromoCodeInput } from '../../CheckoutView'
import { OrderSummary } from '../../Auth/OrderSummary'
import { YEAR, TRIAL } from '../../utils/stripePrices'
import ROUTES from '../../utils/routes'
import { userStore } from '../../utils/UserContext'
import useAccountSubscription from '../useAccountSubscription'
import { PricingType } from '../../../redux/pricing/types'

type LocationState = { plan: PricingType }

const PaymentPage: FC = () => {
  const {
    subscriptionInfo,
    actions: {
      loadSubscriptionInfo, startUserSubscription, upgradeUserSubscription, getProration
    }
  } = useAccountSubscription()

  const { user } = useContext(userStore)
  const history = useHistory()
  const { addNotification } = useNotifications()
  const location = useLocation<LocationState>()
  const { plan } = location.state
  const isTrialUser = user?.trial && user?.stripeSubscriptionStatus === 'Active'

  const [discountAmount, setDiscountAmount] = useState(plan.sale?.value || 0)
  const [discountType, setDiscountType] = useState('')
  const [proratedPrice, setProratedPrice] = useState(0)
  const [discountCode, setDiscountCode] = useState(plan.sale?.id || '')
  const [processing, setProcessing] = useState(false)
  const [cardIsAttached, setCardIsAttached] = useState(false)
  const [trialDiscountCheckbox, setTrialDiscountCheckbox] = useState(false)

  const monthBreakdown = {
    label: 'Monthly Subscription',
    price: proratedPrice || plan.price,
    subLabel: 'Billed monthly'
  }
  const yearBreakdown = {
    label: 'Annual Subscription',
    price: proratedPrice || plan.price,
    subLabel: 'Billed yearly'
  }
  const trialBreakdown = { label: 'Free Trial', price: 0, subLabel: 'Ends in 14 days' }

  const intervalToBreakdown = useMemo(() => {
    switch (plan?.interval) {
      case YEAR:
        return yearBreakdown
      case TRIAL:
        return trialBreakdown
      default:
        return monthBreakdown
    }
  }, [proratedPrice])

  const checkIfProrated = async () => {
    if (subscriptionInfo) {
      try {
        const newCost = await getProration(plan.interval)
        setProratedPrice(newCost)
      }
      catch (err) {
        addNotification({ message: err.message, type: 'error', interval: 5000 })
      }
    }
  }

  const handlePayment = async () => {
    setProcessing(true)

    try {
      if (user?.stripeSubscriptionStatus === 'Inactive') {
        await startUserSubscription(plan.interval, discountCode)
        addNotification({
          message: 'You have successfully subscribed!',
          type: 'success',
          interval: 5000
        })
        window.dataLayer?.push({
          event: 'User Resubscribed',
          conversionValue: intervalToBreakdown.price
        })
      }
      else {
        await upgradeUserSubscription(
          plan.interval,
          discountCode,
          intervalToBreakdown.price - discountAmount
        )
        addNotification({
          message: 'You have successfully upgraded your subscription!',
          type: 'success',
          interval: 5000
        })
        window.dataLayer?.push({
          event: 'User Upgraded To Annual',
          conversionValue: intervalToBreakdown.price - discountAmount
        })
      }

      if (isTrialUser) {
        window.dataLayer?.push({
          event: 'User Upgraded From Trial',
          conversionValue: intervalToBreakdown.price
        })
      }

      history.push(ROUTES.dashboard.home)
    }
    catch (err) {
      addNotification({ message: err.message, type: 'error', interval: 5000 })
    }
  }

  useEffect(() => {
    loadSubscriptionInfo()
  }, [])

  useEffect(() => {
    // set discount if active trial user
    if (isTrialUser && !plan.sale?.id) {
      switch (plan.interval) {
        case YEAR:
          setDiscountCode('trial-to-year')
          setDiscountAmount(50)
          setDiscountType('year-trial-discount')
          return
        default:
          setDiscountCode('trial-to-month')
          setDiscountAmount(15)
          setDiscountType('month-trial-discount')
          return
      }
    }

    // check if there is a prorated price
    checkIfProrated()
  }, [subscriptionInfo?.id])

  if (!plan) {
    return <Redirect to={ROUTES.user.settings.plan} />
  }

  return (
    <StyledPaymentPage>
      <div className="back-button-wrapper">
        <button onClick={() => history.push(ROUTES.user.settings.plan)} className="back-button">
          <ChevronLeftIcon /> Subscription Plan
        </button>
      </div>
      <div className="page-wrap">
        <h1>Payment Method</h1>
        <UpdateCardInfo onCreditCardChange={setCardIsAttached} />
        <div className="order-summary-container">
          <LabelWrapper label="Order Summary">
            <OrderSummary
              discountAmount={discountAmount}
              items={[intervalToBreakdown]}
              discountType={discountType}
            />
          </LabelWrapper>
        </div>
        <PromoCodeInput
          totalCost={intervalToBreakdown.price}
          onChange={(e) => {
            setDiscountAmount(e.discountAmount)
            setDiscountCode(e.stripeID)
            setDiscountType('promo-code')
          }}
        />

        {isTrialUser && (
          <div className="checkbox-container">
            <Checkbox
              name="trial-discount-acknowledgement"
              label={`I acknowledge that upon purchase of ${
                plan.interval === 'Month' ? 'a monthly' : 'an annual'
              } subscription to Social Curator, I will be charged $${intervalToBreakdown.price -
                discountAmount} for the first ${
                plan.interval === 'Month' ? 'month' : 'year'
              } and then $${intervalToBreakdown.price} ${
                plan.interval === 'Month' ? 'monthly' : 'yearly'
              } thereafter.`}
              onChange={() => setTrialDiscountCheckbox(!trialDiscountCheckbox)}
              isChecked={trialDiscountCheckbox}
            />
          </div>
        )}
        <Button
          type="large"
          className="confirm-button"
          onClick={handlePayment}
          isLoading={processing}
          isDisabled={processing || !cardIsAttached || (!trialDiscountCheckbox && isTrialUser)}
        >
          Confirm Subscription
        </Button>
      </div>
    </StyledPaymentPage>
  )
}

export default PaymentPage
